import React, { useCallback, useState } from "react";
import { Drawer, List, Button, Typography, Space, Badge, notification, Card, Avatar, Tag, Row, Col, Divider, Modal } from "antd";
import { useNotificationStore } from "../../stores/notificationStore";
import { BellOutlined, CalendarOutlined, ClockCircleOutlined, EnvironmentOutlined, InfoCircleOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons";
import { useFirebaseNotifications } from "../../hooks/FCM/hooks/useFirebaseNotifications";
import { useUserContextHook } from "../../hooks/useUserContextHook";
import { v4 as uuidv4 } from "uuid";
import { useBackgroundNotificationListener } from "../../hooks/FCM/hooks/useBackgroundNotificationListener ";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FaTruckFast } from "react-icons/fa6";
import { TbRouteAltLeft, TbTruckOff } from "react-icons/tb";
import { LuFolderClock } from "react-icons/lu";
import NotificationMapComponent from "./NotificationMapComponent";

const { Text } = Typography;

const NotificationDrawer = () => {
  const uuid = uuidv4();
  const { employeeDetail } = useUserContextHook();
  const [visible, setVisible] = useState(false);
  const [IdleMapItem, setIdleMapItem] = useState<{ isModalOpen: boolean; list: any }>({
    isModalOpen: false,
    list: null
  });
  const navigate = useNavigate();

  const notifications = useNotificationStore((state) => state.notifications);
  const notificationCount = useNotificationStore((state) => state.notificationCount);
  const addNotification = useNotificationStore((state) => state.addNotification);
  const clearNotifications = useNotificationStore((state) => state.clearNotifications);
  const clearNotificationCount = useNotificationStore((state) => state.clearNotificationCount);
  // const notificationHistory = useNotificationStore((state) => state.notificationHistory);
  // const { addNotificationToHistory } = useNotificationStore.getState();

  const handleNotificationClick = useCallback(() => {
    setVisible(true);
    clearNotificationCount();
  }, [clearNotificationCount]);

  const closeDrawer = () => {
    setVisible(false);
  };

  // Filter unread notifications
  const unreadNotifications = notifications.filter((n) => !n.read);

  // Notification hook
  useFirebaseNotifications(
    {
      uuid,
      userId: employeeDetail?.employeeId,
      positionId: employeeDetail?.positionId,
      stationId: employeeDetail?.stationId,
    },
    (payload) => {
      console.log("FCM Message:", payload);
      const { title, body } = payload.notification!;
      const config = payload.data?.notificationType ? typeConfig[payload.data?.notificationType] : typeConfig['DEFAULT'];
      addNotification({
        id: payload.messageId!,
        title: title || "No Title",
        body: body || "No Body",
        data: payload.data,
      });


      notification.open({
        icon: config.icon,
        message: payload.notification?.title ?? "New Notification",
        description: payload.notification?.body,
        placement: "topRight",
        duration: 5,
        style: {
          borderRadius: 10,

          /* 3D border */
          borderStyle: 'solid',
          borderWidth: '3px 1px 1px 3px',
          borderTopColor: config.color,
          borderLeftColor: config.color,
          borderRightColor: 'rgba(255,255,255,0.45)',
          borderBottomColor: 'rgba(255,255,255,0.45)',

          /* depth */
          boxShadow: '2px 2px 8px rgba(0,0,0,0.2)',

          padding: '10px 12px', // compact
        },
      });

    }
  );

  useBackgroundNotificationListener();

  const baseStyle = {
    wrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 18,
    },
  };

  const typeConfig = {
    MOVING: {
      color: 'green',
      icon: (
        <div style={baseStyle.wrapper}>
          <FaTruckFast />
        </div>
      ),
      backgroundColor: '#dcfce7',
    },

    IDLE: {
      color: 'red',
      icon: (
        <div style={baseStyle.wrapper}>
          <TbTruckOff />
        </div>
      ),
      backgroundColor: '#fee2e2',
    },
    ROUTE_DEVIATION: {
      color: 'orange',
      icon: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}><TbRouteAltLeft /></div>,
      backgroundColor: '#fef9c3',
    },

    ROUTE_DEVIATION_WARNING: {
      color: 'orange',
      icon: (
        <div style={baseStyle.wrapper}>
          <TbRouteAltLeft />
        </div>
      ),
      backgroundColor: '#fef9c3',
    },

    ROUTE_DEVIATION_CRITICAL: {
      color: 'red',
      icon: (
        <div style={baseStyle.wrapper}>
          <TbRouteAltLeft />
        </div>
      ),
      backgroundColor: '#fee2e2',
    },

    POD_FAIL: {
      color: 'red',
      icon: (
        <div style={baseStyle.wrapper}>
          <LuFolderClock />
        </div>
      ),
      backgroundColor: '#fee2e2',
    },

    POD: {
      color: 'red',
      icon: (
        <div style={baseStyle.wrapper}>
          <LuFolderClock />
        </div>
      ),
      backgroundColor: '#fee2e2',
    },
    POD_DELIVERY: {
      color: 'red',
      icon: (
        <div style={baseStyle.wrapper}>
          <LuFolderClock />
        </div>
      ),
      backgroundColor: '#fee2e2',
    },

    POD_PICKUP: {
      color: 'red',
      icon: (
        <div style={baseStyle.wrapper}>
          <LuFolderClock />
        </div>
      ),
      backgroundColor: '#fee2e2',
    },

    DEFAULT: {
      color: 'blue',
      icon: (
        <div style={baseStyle.wrapper}>
          <InfoCircleOutlined />
        </div>
      ),
      backgroundColor: '#dbeafe',
    },
  };

  const modalTitle = IdleMapItem?.list ? (
    <Space size="small" wrap>
      <span style={{ fontWeight: 600, fontSize: 15 }}>Idle Location</span>

      <Tag color="blue">Driver: {IdleMapItem.list?.driverUserName}</Tag>

      <Tag color="geekblue">
        Vehicle: {IdleMapItem.list.vehiclePlateNumber}
      </Tag>

      {/* <Tag color="orange">
         Duration:{' '}
         {IdleMapItem.list.idleMinutes
           ? Math.floor(IdleMapItem.list.idleMinutes)
           : 0}{' '}
         min
       </Tag> */}

      <Tag>
        From:{' '}
        {moment
          (Number(IdleMapItem.list?.previousTime))
          .format('DD MMM, hh:mm A')}
      </Tag>

      <Tag>
        To:{' '}
        {moment
          (Number(IdleMapItem.list?.deviceTime))
          .format('DD MMM, hh:mm A')}
      </Tag>
    </Space>
  ) : (
    'Idle Location'
  );

  return (
    <>
      {/* <Badge style={{ marginRight: 10 }} count={notificationCount} overflowCount={99} offset={[0, 10]}>
                <Button
                    type="text"
                    icon={<BellOutlined style={{ fontSize: 22 }} />}
                    onClick={handleNotificationClick}
                />
            </Badge> */}

      <Badge
        count={notificationCount}
        overflowCount={99}
        offset={[0, 10]}
        className="notification-badge"
      >
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 22 }} />}
          onClick={handleNotificationClick}
        />
      </Badge>

      <Drawer
        title={
          <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: 18 }}>
              {' '}
              {`Notifications ${unreadNotifications.length
                  ? `(${unreadNotifications.length})`
                  : ''
                }`}
            </Text>
            <Button
              type="primary"
              onClick={() => {
                navigate('/notifications-list');
                closeDrawer();
              }}
            >
              View All
            </Button>
          </Space>
        }
        placement="right"
        width={360}
        onClose={closeDrawer}
        open={visible}
        bodyStyle={{
          padding: '4px 0px 4px 4px',
          backgroundColor: '#f5f5f7',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
        drawerStyle={{ borderRadius: '16px 0 0 16px' }}
      >
        {/* Scrollable list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: 16,
            paddingRight: 16
          }}
        >
          <List
            dataSource={unreadNotifications}
            renderItem={(item) => {
              const config = typeConfig[item.data?.notificationType]?.color
                ? typeConfig[item.data?.notificationType]
                : typeConfig['DEFAULT'];

              return (
                <List.Item
                  style={{
                    padding: 0,
                    marginBottom: 6,
                    border: 'none'
                  }}
                >
                  <Card
                    hoverable
                    style={{
                      width: '100%',
                      // borderLeft: `4px solid ${config.color}`,
                      // background: config.bgColor,
                      border: '1px solid #e0e0e0',
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    bodyStyle={{ padding: '8px' }}
                  >
                    <Row align="middle" gutter={16} style={{ marginBottom: 12 }}>
                      <Col>
                        <Tag
                          color={config.color}
                          style={{
                            margin: 0,
                            height: 24,
                            fontSize: 12,
                            padding: '0 8px',
                            display: 'flex',
                            alignItems: 'center',
                            background: config.color,
                            color: '#fff',

                            gap: 4
                          }}
                        >
                          {config.icon}
                          {item.data?.notificationType || 'INFO'}
                        </Tag>
                      </Col>

                      <Col
                        flex="auto"
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <Space direction="horizontal" size={10}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {moment(
                              Number(item?.data?.deviceTime || item.timestamp)
                            ).format('DD MMM, h:mm A')}
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                    <Row>
                      <Col flex={'auto'}>
                        <Space direction="horizontal" size={10}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            VEHICLE :{' '}
                            <Tag icon={<span>🚚</span>} color="geekblue">
                              {item?.data?.vehiclePlateNumber || 'N/A'}
                            </Tag>
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          PHONE :{' '}
                          <Tag icon={<span>📞</span>} color="geekblue">
                            {item?.data?.driverPhone || 'N/A'}
                          </Tag>
                        </Text>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '12px 0' }} />
                    <Row
                      align="middle"
                      wrap={false}
                      gutter={8}
                      style={{ marginBottom: 12 }}
                    >
                      <Col flex="auto">
                        <Tag icon={<span>👤</span>} color="green">
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item?.data?.driverName || 'N/A'}
                          </span>
                        </Tag>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '12px 0' }} />
                    <Row align="middle" wrap={false}>
                      <Col flex="auto">
                        {item?.data?.message && (
                          <Text
                            style={{
                              fontSize: 13,
                              lineHeight: 1.3,
                              display: 'block',
                              wordBreak: 'break-word'
                            }}
                          >
                            {item?.data?.message || item.body}
                          </Text>
                        )}
                      </Col>
                      {(item.data?.notificationType === 'IDLE' || item.data?.notificationType === 'MOVING') && (
                        <Col flex="none">
                          <Button
                            size="middle"
                            icon={<EnvironmentOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log({ item });
                              setIdleMapItem({
                                isModalOpen: true,
                                list: item?.data
                              });
                            }}
                            style={{
                              padding: '0 8px',
                              height: 24,
                              marginLeft: 8
                            }}
                          />
                        </Col>
                      )}
                    </Row>
                  </Card>
                </List.Item>
                //   <Card
                //       key={item.id}
                //       hoverable
                //       bodyStyle={{
                //           padding: '10px 12px', // ⬅ reduce default 24px
                //       }}
                //       style={{
                //           marginBottom: 8,
                //           borderRadius: 10,

                //           /* 3D border */
                //           borderStyle: 'solid',
                //           borderWidth: '3px 1px 1px 3px',
                //           borderTopColor: config.color,
                //           borderLeftColor: config.color,
                //           borderRightColor: 'rgba(255,255,255,0.4)',
                //           borderBottomColor: 'rgba(255,255,255,0.4)',

                //           boxShadow: '1px 1px 4px rgba(0,0,0,0.12)',
                //       }}
                //   >
                //       <Card.Meta
                //           avatar={
                //               <Tag
                //                   color={config.color}
                //                   style={{
                //                       margin: 0,
                //                       height: 24,           // ⬅ smaller
                //                       fontSize: 12,
                //                       padding: '0 8px',
                //                       display: 'flex',
                //                       alignItems: 'center',
                //                       gap: 4,
                //                   }}
                //               >
                //                   {config.icon}
                //                   {item.data?.notificationType || 'INFO'}
                //               </Tag>
                //           }
                //           title={
                //               <Text strong style={{ fontSize: 14 }}>
                //                   {item.title}
                //               </Text>
                //           }
                //           description={
                //               <Space direction="vertical" size={2} style={{ width: '100%' }}>
                //                   {/* Message */}
                //                   <Text style={{ fontSize: 13, lineHeight: 1.3 }}>
                //                       {item?.data?.message || item.body}
                //                   </Text>

                //                   {/* Driver + Vehicle in same row */}
                //                   <Space size={8} wrap>
                //                       <Text type="secondary" style={{ fontSize: 11 }}>
                //                           👮‍♂️ <b>{item?.data?.driverUserName}</b>
                //                       </Text>
                //                       <Text type="secondary" style={{ fontSize: 11 }}>
                //                           🚚 <b>{item?.data?.vehiclePlateNumber}</b>
                //                       </Text>
                //                   </Space>

                //                   {/* Time */}
                //                   <Text type="secondary" style={{ fontSize: 10 }}>
                //                       <ClockCircleOutlined />{' '}
                //                       {moment(Number(item?.data?.deviceTime || item.timestamp))
                //                           .format('DD MMM, h:mm A')}
                //                   </Text>
                //               </Space>
                //           }
                //       />
                //   </Card>
              );
            }}
          />
        </div>

        {/* Buttons fixed at bottom */}
        <Space
          style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px' }}
        >
          <Button onClick={clearNotifications}>Clear All</Button>
          {/* <Button type="primary" onClick={() => { navigate('/notifications-list'); closeDrawer(); }}>
                        View All
                    </Button> */}
        </Space>
      </Drawer>
      <Modal
        maskClosable={false}
        open={IdleMapItem?.isModalOpen}
        footer={null}
        title={modalTitle}
        onCancel={() => setIdleMapItem({ isModalOpen: false, list: null })}
        width="90vw"
        bodyStyle={{
          height: 'calc(100vh - 120px)',
          overflow: 'auto',
          padding: 0,
          margin: 0
        }}
        centered
      >
        <NotificationMapComponent MarkerItem={IdleMapItem?.list} />
      </Modal>
    </>
  );
};

export default NotificationDrawer;
